import Logo from '@avanta/shared/assets/avanta-logo.svg';
import { Button } from '@avanta/shared/components/ui/Button';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { LogOut, MenuIcon, Settings, User, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Link, useLocation, useNavigate } from 'react-router';
import { SupportButton } from 'components/SupportButton';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from 'components/ui/Sheet';
import { ROUTES } from 'constants/routes';
import { useUserStore } from 'stores/user';
import { scrollToTop } from 'utils/common';
import { useNavActions, useScrollCompactNavBar } from '../../hooks';

const SHEET_ACCOUNT_ROW =
  'flex h-10 w-full cursor-pointer items-center gap-2 rounded-none px-2 py-2 text-left outline-none transition-colors hover:bg-plum-100/70 focus-visible:bg-plum-100/70 focus-visible:ring-2 focus-visible:ring-teal-500/30 focus-visible:ring-inset';

export const HamburgerMenu = (): React.ReactElement => {
  const isCompact = useScrollCompactNavBar();
  const [open, setOpen] = useState(false);
  const closeMenu = (): void => setOpen(false);
  const navigate = useNavigate();
  const pathname = useLocation().pathname;
  const user = useUserStore(state => state.user);
  const {
    greetingLabel,
    primaryNavRoutes,
    primaryNavLinkLabel,
    showScheduleButton,
    showSupportButton,
    handleProfile,
    handleLogout,
  } = useNavActions({ onNavigate: closeMenu });
  const { t } = useTranslation(LOCIZE_NS.COMMON);
  const isProfileActive = pathname === ROUTES.PROFILE || pathname.startsWith(`${ROUTES.PROFILE}/`);

  const handleScheduleSession = (): void => {
    if (!user?.profileCompleteToBookAppointments) {
      navigate(ROUTES.APPOINTMENTS_PERSONAL_DATA);
      closeMenu();
      return;
    }
    navigate(ROUTES.APPOINTMENTS_RESERVE);
    closeMenu();
  };

  const handleOnClick = (): void => {
    closeMenu();
    scrollToTop();
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex h-12 min-h-12 items-center justify-end rounded-bl-xl pl-4 pr-5 transition-colors duration-200',
            isCompact ? 'bg-white' : 'bg-transparent'
          )}
        >
          <MenuIcon className="size-8 shrink-0 text-denim-500" strokeWidth={1.5} />
        </button>
      </SheetTrigger>
      <SheetContent className="z-110 w-[90vw] border-l-0 p-0 [&>button]:hidden">
        <VisuallyHidden>
          <SheetTitle>{t('Common.menu')}</SheetTitle>
          <SheetDescription>{t('Common.menuDescription')}</SheetDescription>
        </VisuallyHidden>
        <div className="flex h-full flex-col pb-8">
          <div className="relative px-6 pt-2">
            <Link to={ROUTES.HOME} onClick={handleOnClick} className="inline-flex items-center">
              <img src={Logo} alt="Avanta Mynd Logo" className="h-8 w-24" />
            </Link>
            <SheetClose asChild>
              <button type="button" className="absolute top-2 right-4 text-denim-500">
                <X className="size-8" strokeWidth={1.5} />
              </button>
            </SheetClose>
          </div>
          <div className="mt-12">
            {primaryNavRoutes.map(route => (
              <div key={route.href} className="border-b border-plum-200 px-6 py-1">
                <NavLink
                  end
                  to={route.href}
                  onClick={handleOnClick}
                  className={({ isActive }) =>
                    cn(
                      'flex h-10 items-center px-2',
                      isActive ? 'bg-mint-100 text-teal-500' : 'text-denim-500'
                    )
                  }
                >
                  <span className="text-base font-medium leading-5">
                    {primaryNavLinkLabel(route)}
                  </span>
                </NavLink>
              </div>
            ))}
          </div>
          <div className="mt-10 px-6">
            <div className="mb-4 flex items-center gap-1 text-denim-500">
              <User className="size-4 shrink-0 text-denim-500" aria-hidden />
              <span className="text-sm font-medium leading-4 text-denim-500">{greetingLabel}</span>
            </div>
            <div className="divide-y divide-plum-100 overflow-hidden rounded-xl border border-plum-100 bg-white">
              <button
                type="button"
                className={cn(
                  SHEET_ACCOUNT_ROW,
                  isProfileActive ? 'bg-mint-100 text-teal-500' : 'bg-white text-denim-500'
                )}
                onClick={handleProfile}
              >
                <Settings className="size-4 shrink-0 text-teal-600" aria-hidden />
                <span className="text-sm font-normal leading-4">{t('Nav.profile')}</span>
              </button>
              <button
                type="button"
                className={cn(SHEET_ACCOUNT_ROW, 'bg-white text-denim-500')}
                onClick={handleLogout}
              >
                <LogOut className="size-4 shrink-0 text-teal-600" />
                <span className="text-sm font-normal leading-4">{t('Nav.logout')}</span>
              </button>
            </div>
          </div>
          <div className="mt-auto px-4">
            <div className="flex flex-col gap-4">
              {showScheduleButton && (
                <Button size="lg" className="w-full" onClick={handleScheduleSession}>
                  {t('AppointmentsBanner.cta', { ns: LOCIZE_NS.APPOINTMENTS })}
                </Button>
              )}
              {showSupportButton && <SupportButton onAfterClick={closeMenu} />}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
